import { mergeDeep } from "remeda"
import { getErrorCode } from "@krakentech/blueprint-api"
import { AffiliateCode } from "@/constants/affiliateCode"
import { ERROR_CODE } from "@/constants/errorCode"
import { MarketName } from "@/constants/marketName"
import { ProviderCalendarId } from "@/entities/providerCalendar"
import { useRunEffyConsumptionAnalysis } from "@/features/effy/hooks/useRunEffyConsumptionAnalysis"
import {
    ONBOARDING_DEFAULT_FORM_VALUES,
    type OnboardingState,
    useOnboardingToStep,
} from "@/features/onboarding/wizard"
import {
    EnergyType,
    getOnboardingConfig,
    hasElectricity,
    hasGas,
    isElectricityOnly,
    isGasOnly,
} from "@/features/subscription"
import {
    parseElectricityMeterPointEligibility,
    parseEligibilityErrors,
    parseGasMeterPointEligibility,
} from "@/features/subscription/meterEligibility"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { type JourneyType } from "@/scalars/journeyType"
import { onboardingDataLayer, OnboardingDataLayerEvent } from "../../datalayer"
import { useRunMeterPointPmaxAnalysis } from "../../meterPower"
import { useRequestQuote } from "../../requestQuote"
import { isFastQuoteJourney } from "../../utils/onboardingVariant"
import { useInitialOnboardingFormContext } from "../context/InitialOnboardingFormContext"
import { useCheckMeterPointEligibility } from "./useCheckMeterPointEligibility"
import { useCoconRequestQuote } from "./useCoconRequestQuote"

function isDifferentSupplyPointAddressesError(error: unknown) {
    return getErrorCode(error) === ERROR_CODE.DIFFERENT_ADDRESS_FORM_SUPPLY_POINTS
}

type MeterPoints = Array<{
    meterPointId: string
    marketName: MarketName
    journeyType: JourneyType
    givenName: string
    familyName: string
    hasChangeOfSupplierError?: boolean
}>

type UseEligibilityCheckQuoteProps = {
    stepName: OnboardingRouteNames
}

export function useEligibilityCheckQuote(props: UseEligibilityCheckQuoteProps) {
    const { toStep, toNextStep } = useOnboardingToStep(props.stepName)
    const { setInputMethod, setMeterPoints } = useInitialOnboardingFormContext()

    const checkMeterPointsEligibility = useCheckMeterPointEligibility()
    const [handleRunMeterPointPmaxAnalysis] = useRunMeterPointPmaxAnalysis()
    const [handleRunEffyConsumptionAnalysis] = useRunEffyConsumptionAnalysis()
    const [handleRequestQuote, requestQuote] = useRequestQuote(props)
    const [handleCoconRequestQuote, coconRequestQuote] = useCoconRequestQuote(props)

    async function validateMeterPoints(
        meterPointsProps: MeterPoints,
        onboardingState: OnboardingState
    ) {
        const newOnboardingState = structuredClone(onboardingState)
        const meterPoints = structuredClone(meterPointsProps)

        newOnboardingState.formValues.inputMethod = "meterPoint"
        newOnboardingState.formValues.givenName = meterPoints[0].givenName
        newOnboardingState.formValues.familyName = meterPoints[0].familyName

        const { formValues } = newOnboardingState

        const config = getOnboardingConfig({
            salesChannel: formValues.affiliateOrganisationSalesChannel,
            affiliateCode: formValues.affiliateCode,
            affiliateOrganisation: formValues.affiliateOrganisationName,
        })

        onboardingDataLayer.sendTrackingEvent(
            OnboardingDataLayerEvent.MeterConfirmationAll,
            newOnboardingState.formValues
        )

        // Gas only case -> feed electricity meter point id to meterPoints array
        // This handle DifferentAddressesError if gas and electricity addresses aren't the same
        if (
            isGasOnly(formValues.selectedEnergyTypes) &&
            !!formValues.propertyId &&
            formValues.electricity.supplyPoint.meterPoint.id
        ) {
            meterPoints.push({
                meterPointId: formValues.electricity.supplyPoint.meterPoint.id,
                marketName: formValues.electricity.marketName,
                journeyType: formValues.journeyType,
                givenName: formValues.electricity.givenName,
                familyName: formValues.electricity.familyName,
            })
        }

        try {
            const { eligibilityResults } = await checkMeterPointsEligibility.mutateAsync({
                meterPoints,
                clientConsent: true,
                customerName: {
                    firstName: meterPoints[0].givenName,
                    lastName: meterPoints[0].familyName,
                },
            })

            for (const eligibilityResult of eligibilityResults) {
                const { supplyPoint } = eligibilityResult

                switch (supplyPoint.marketName) {
                    case MarketName.FRA_ELECTRICITY: {
                        if (eligibilityResult.errors?.length) {
                            const eligibilityErrorResult = parseEligibilityErrors({
                                errors: eligibilityResult.errors,
                                meterPointId: supplyPoint.meterPoint.id,
                                externalService: "ENEDIS",
                            })

                            const electricityMeterPoint = meterPoints.find(
                                (meterPoint) => meterPoint.marketName === MarketName.FRA_ELECTRICITY
                            )

                            if (electricityMeterPoint && eligibilityErrorResult) {
                                electricityMeterPoint.hasChangeOfSupplierError =
                                    eligibilityErrorResult.hasChangeOfSupplierError
                            }

                            break
                        }

                        const { billingAddress, electricity } =
                            parseElectricityMeterPointEligibility({
                                ...eligibilityResult,
                                supplyPoint,
                            })

                        let providerCalendarId = electricity.providerCalendarId

                        if (
                            !!config.providerCalendarForced &&
                            config.providerCalendarForced !== electricity.providerCalendarId
                        ) {
                            providerCalendarId = null
                        }

                        // TODO : Delete when CPC is handled correctly
                        if (
                            (formValues.affiliateCode === AffiliateCode.OCTOFLEX ||
                                isFastQuoteJourney(formValues)) &&
                            !providerCalendarId
                        ) {
                            providerCalendarId = ProviderCalendarId.BASE
                        }

                        newOnboardingState.formValues = mergeDeep(newOnboardingState.formValues, {
                            billingAddress,
                            electricity: {
                                ...electricity,
                                providerCalendarId,
                            },
                            userInput: {
                                ...formValues.userInput,
                                electricitySubscribedMaxPower: null,
                            },
                        })

                        if (
                            hasElectricity(formValues.selectedEnergyTypes) &&
                            config.meterPowerAnalysisEnabled &&
                            formValues.journeyType === "CHANGE_OF_SUPPLIER"
                        ) {
                            const meterPowerAnalysis = await handleRunMeterPointPmaxAnalysis(
                                newOnboardingState.formValues
                            )

                            if (meterPowerAnalysis) {
                                newOnboardingState.formValues = mergeDeep(
                                    newOnboardingState.formValues,
                                    {
                                        meterPowerAnalysis,
                                    }
                                )
                            }
                        }

                        if (
                            hasElectricity(formValues.selectedEnergyTypes) &&
                            formValues.affiliateCode === AffiliateCode.OFFRE_EFFY &&
                            formValues.journeyType === "CHANGE_OF_SUPPLIER"
                        ) {
                            const effyAnalysis = await handleRunEffyConsumptionAnalysis({
                                meterPointId: supplyPoint.meterPoint.id,
                                firstName: formValues.givenName,
                                lastName: formValues.familyName,
                            })

                            newOnboardingState.formValues.effy.prefilledConsumption =
                                effyAnalysis.consumption

                            if (effyAnalysis.token) {
                                newOnboardingState.formValues.enedisAnalysisToken =
                                    effyAnalysis.token
                            }
                        }

                        break
                    }

                    case MarketName.FRA_GAS: {
                        if (eligibilityResult.errors?.length) {
                            const eligibilityErrorResult = parseEligibilityErrors({
                                errors: eligibilityResult.errors,
                                meterPointId: supplyPoint.meterPoint.id,
                                externalService: "GRDF",
                            })

                            const gasMeterPoint = meterPoints.find(
                                (meterPoint) => meterPoint.marketName === MarketName.FRA_GAS
                            )

                            if (gasMeterPoint && eligibilityErrorResult) {
                                gasMeterPoint.hasChangeOfSupplierError =
                                    eligibilityErrorResult.hasChangeOfSupplierError
                            }

                            break
                        }

                        const { gas, billingAddress: gasBillingAddress } =
                            parseGasMeterPointEligibility({
                                ...eligibilityResult,
                                supplyPoint,
                            })

                        if (isGasOnly(formValues.selectedEnergyTypes)) {
                            newOnboardingState.formValues = mergeDeep(
                                newOnboardingState.formValues,
                                { billingAddress: gasBillingAddress }
                            )
                        }

                        newOnboardingState.formValues = mergeDeep(newOnboardingState.formValues, {
                            gas,
                        })

                        break
                    }
                }
            }

            if (meterPoints.some((mp) => mp.hasChangeOfSupplierError)) {
                setInputMethod("meterPoint")
                setMeterPoints(meterPoints)

                return
            }

            const ineligibleEnergyTypes = eligibilityResults.reduce<MarketName[]>(
                (acc, eligibilityResult) => {
                    if (!eligibilityResult.isEligible) {
                        acc.push(eligibilityResult.supplyPoint.marketName)
                    }

                    return acc
                },
                []
            )

            if (
                ineligibleEnergyTypes.includes(MarketName.FRA_ELECTRICITY) &&
                ineligibleEnergyTypes.includes(MarketName.FRA_GAS)
            ) {
                newOnboardingState.formValues.electricity = structuredClone(
                    ONBOARDING_DEFAULT_FORM_VALUES.electricity
                )

                newOnboardingState.formValues.gas = structuredClone(
                    ONBOARDING_DEFAULT_FORM_VALUES.gas
                )

                // TODO handle dual ineligible with a new step
                // return await toStep(
                //     OnboardingRouteNames.DualMeterIneligible,
                //     newOnboardingState
                // )
            }

            if (ineligibleEnergyTypes.includes(MarketName.FRA_ELECTRICITY)) {
                newOnboardingState.formValues.electricity = structuredClone(
                    ONBOARDING_DEFAULT_FORM_VALUES.electricity
                )

                return await toStep(
                    OnboardingRouteNames.ElectricityMeterIneligible,
                    newOnboardingState
                )
            }

            if (ineligibleEnergyTypes.includes(MarketName.FRA_GAS)) {
                newOnboardingState.formValues.gas = structuredClone(
                    ONBOARDING_DEFAULT_FORM_VALUES.gas
                )

                return await toStep(OnboardingRouteNames.GasMeterIneligible, newOnboardingState)
            }

            for (const meterPoint of meterPoints) {
                const energyType =
                    meterPoint.marketName === MarketName.FRA_ELECTRICITY
                        ? EnergyType.Electricity
                        : EnergyType.Gas

                newOnboardingState.formValues[energyType].givenName = meterPoint.givenName
                newOnboardingState.formValues[energyType].familyName = meterPoint.familyName
            }

            if (isElectricityOnly(formValues.selectedEnergyTypes)) {
                newOnboardingState.formValues.gas = structuredClone(
                    ONBOARDING_DEFAULT_FORM_VALUES.gas
                )
            }

            if (isGasOnly(formValues.selectedEnergyTypes)) {
                newOnboardingState.formValues.electricity = structuredClone(
                    ONBOARDING_DEFAULT_FORM_VALUES.electricity
                )
            }

            if (hasElectricity(formValues.selectedEnergyTypes)) {
                // Skip providerCalendar step for Direct Journey when flag low-input or low-input-customizable
                if (isFastQuoteJourney(formValues)) {
                    if (config.hasCPCQuoting) {
                        return await handleCoconRequestQuote(newOnboardingState)
                    }

                    if (formValues.journeyType === "COMMISSIONING") {
                        return await toStep(
                            OnboardingRouteNames.EnergyLandscape,
                            newOnboardingState
                        )
                    }

                    return await handleRequestQuote({ onboardingState: newOnboardingState })
                }

                // Skip providerCalendar step for Octoflex journey
                // TODO : Delete when CPC is handled correctly
                if (formValues.affiliateCode === AffiliateCode.OCTOFLEX) {
                    return await handleRequestQuote({ onboardingState: newOnboardingState })
                }

                return await toNextStep(newOnboardingState)
            }

            if (hasGas(formValues.selectedEnergyTypes)) {
                if (formValues.journeyType === "COMMISSIONING") {
                    return await toStep(OnboardingRouteNames.EnergyLandscape, newOnboardingState)
                }

                return await handleRequestQuote({ onboardingState: newOnboardingState })
            }
        } catch (error: unknown) {
            if (isDifferentSupplyPointAddressesError(error)) {
                return await toStep(OnboardingRouteNames.AddressIneligible, newOnboardingState)
            }

            throw error
        }
    }

    const checkMeterPointsEligibilityError = !isDifferentSupplyPointAddressesError(
        checkMeterPointsEligibility.error
    )
        ? checkMeterPointsEligibility.error
        : undefined

    return {
        isPending:
            checkMeterPointsEligibility.isPending ||
            requestQuote.isPending ||
            coconRequestQuote.isPending,
        error: checkMeterPointsEligibilityError || requestQuote.error || coconRequestQuote.error,
        validateMeterPoints,
    }
}
